/**
 * Payment methods management section for the portal Account page.
 *
 * Lists saved payment methods with delete capability and opens the
 * add form in a Sidebar panel (consistent with the Cart page flow).
 * Fetches data through portal-specific endpoints — no CRM services imported.
 */

import {
	faCreditCard,
	faPlus,
	faStar as faStarRegular,
	faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PortalPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import { Sidebar, useTranslation } from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPaymentService from '../services/portalPaymentService';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

interface PaymentMethodsSectionProps {
	/** When true, auto-open the add sidebar on mount (e.g. from cart page link) */
	autoOpenAdd?: boolean;
	/**
	 * Hide the card's own "Payment Methods" title row (icon + heading +
	 * Add link). The dedicated Payment Methods PAGE already titles itself
	 * (MobilePageHeader / h1) and owns the Add button in that header row,
	 * so the in-card copy would be a duplicate there. Embedded contexts
	 * (Account page card) keep the default self-labelled header.
	 */
	hideHeader?: boolean;
	/**
	 * Controlled visibility for the add sidebar — lets a parent-owned Add
	 * button (the page header's) drive the same sidebar. When provided,
	 * `onAddOpenChange` must be too; internal triggers (empty-state Add)
	 * route through it. Omit both for self-managed state.
	 */
	addOpen?: boolean;
	onAddOpenChange?: (open: boolean) => void;
}

export const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
	autoOpenAdd = false,
	hideHeader = false,
	addOpen,
	onAddOpenChange,
}) => {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	const [methods, setMethods] = useState<Array<PortalPaymentMethodDto>>([]);
	const [supportedTypes, setSupportedTypes] = useState<
		Array<SupportedPaymentMethodType>
	>([]);
	const [loading, setLoading] = useState(true);
	const [internalShowAdd, setInternalShowAdd] = useState(autoOpenAdd);
	const showAddSidebar = addOpen ?? internalShowAdd;
	const setShowAddSidebar = (open: boolean): void => {
		if (onAddOpenChange != null) {
			onAddOpenChange(open);
		} else {
			setInternalShowAdd(open);
		}
	};
	const [deletingId, setDeletingId] = useState<number | null>(null);
	const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(null);

	const fetchMethods = useCallback(async () => {
		if (token == null) {
			return;
		}

		try {
			const response = await portalPaymentService.getPaymentMethods(token);
			if (response.success) {
				setMethods(response.data);
			}
		} catch (err) {
			console.error('Failed to fetch payment methods:', err);
		}
	}, [token]);

	const fetchSupportedTypes = useCallback(async () => {
		if (token == null) {
			return;
		}

		try {
			const response = await portalPaymentService.getSupportedTypes(token);
			if (response.success) {
				setSupportedTypes(response.data);
			}
		} catch (err) {
			console.error('Failed to fetch supported payment types:', err);
		}
	}, [token]);

	// Fetch methods and supported types on mount
	useEffect(() => {
		const load = async (): Promise<void> => {
			setLoading(true);
			await Promise.all([fetchMethods(), fetchSupportedTypes()]);
			setLoading(false);
		};
		void load();
	}, [fetchMethods, fetchSupportedTypes]);

	const handleDelete = async (methodId: number): Promise<void> => {
		if (token == null) {
			return;
		}

		setDeletingId(methodId);
		try {
			const result = await portalPaymentService.deletePaymentMethod(
				token,
				methodId,
			);
			if (result.success) {
				setMethods((prev) => prev.filter((m) => m.id !== methodId));
			}
		} catch (err) {
			console.error('Failed to delete payment method:', err);
		} finally {
			setDeletingId(null);
		}
	};

	const handleSetPrimary = async (methodId: number): Promise<void> => {
		if (token == null) {
			return;
		}

		setSettingPrimaryId(methodId);
		try {
			const result = await portalPaymentService.setPrimaryPaymentMethod(
				token,
				methodId,
			);
			if (result.success) {
				// Update local state: unset old primary, set new one
				setMethods((prev) =>
					prev.map((m) => ({
						...m,
						mPaymentIsPrimary: m.id === methodId,
					})),
				);
			}
		} catch (err) {
			console.error('Failed to set primary payment method:', err);
		} finally {
			setSettingPrimaryId(null);
		}
	};

	const handleAddSuccess = (): void => {
		setShowAddSidebar(false);
		void fetchMethods();
	};

	/**
	 * Format the display label for a saved payment method.
	 * e.g. "Visa ****1234" or "ACH ****5678"
	 */
	const formatMethodLabel = (method: PortalPaymentMethodDto): string => {
		const subtype = method.mPaymentSubtype;
		const type = method.mPaymentType;
		const lastFour = method.mPaymentLastFour;
		const label = subtype ?? type ?? t('Payment Method');
		const masked = lastFour != null ? ` ****${lastFour}` : '';
		return `${label}${masked}`;
	};

	const formatExpiration = (method: PortalPaymentMethodDto): string | null => {
		if (method.mPaymentExpirationDate == null) {
			return null;
		}
		const date = new Date(method.mPaymentExpirationDate);
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear().toString();
		return `${month}/${year}`;
	};

	if (loading) {
		return (
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				{!hideHeader && (
					<div className="flex items-center gap-2 mb-3">
						<FontAwesomeIcon icon={faCreditCard} className="text-brand-muted" />
						<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
							{t('Payment Methods')}
						</h2>
					</div>
				)}
				<div className="flex items-center justify-center py-6">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-portal-accent" />
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				{!hideHeader && (
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-2">
							<FontAwesomeIcon
								icon={faCreditCard}
								className="text-brand-muted"
							/>
							<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
								{t('Payment Methods')}
							</h2>
						</div>
						<button
							type="button"
							onClick={() => {
								setShowAddSidebar(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:text-portal-accent"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add')}</span>
						</button>
					</div>
				)}

				{/* Empty state (PD-14699) — icon + one-liner + the add action, so
				    the section explains itself instead of a bare text line. */}
				{methods.length === 0 && (
					<div className="text-center py-6">
						<FontAwesomeIcon
							icon={faCreditCard}
							className="text-3xl text-gray-300 dark:text-gray-600 mb-3"
						/>
						<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
							{t('No saved payment methods')}
						</p>
						<p className="text-xs text-brand-muted mt-1">
							{t('Add a card or bank account to make paying invoices faster.')}
						</p>
						<button
							type="button"
							onClick={() => {
								setShowAddSidebar(true);
							}}
							className="mt-3 px-4 py-2 text-sm font-medium rounded-lg border border-brand-border bg-brand-surface hover:border-portal-accent transition-colors min-h-touch"
						>
							{t('Add payment method')}
						</button>
					</div>
				)}

				{methods.length > 0 && (
					<div className="divide-y divide-gray-100 dark:divide-gray-800">
						{methods.map((method) => {
							const exp = formatExpiration(method);
							const isPrimary = method.mPaymentIsPrimary;
							const isDeleting = deletingId === method.id;
							const isSettingPrimary = settingPrimaryId === method.id;

							return (
								<div
									key={method.id}
									className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0"
								>
									{/* Primary star toggle */}
									<button
										type="button"
										onClick={() => {
											if (!isPrimary) {
												void handleSetPrimary(method.id);
											}
										}}
										disabled={isPrimary || isSettingPrimary}
										className="shrink-0 mr-2.5 p-1 disabled:cursor-default transition-colors"
										aria-label={
											isPrimary
												? t('Primary payment method')
												: t('Set as primary')
										}
									>
										{isSettingPrimary ? (
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500" />
										) : (
											<FontAwesomeIcon
												icon={isPrimary ? faStarSolid : faStarRegular}
												className={
													isPrimary
														? 'text-sm text-yellow-500'
														: 'text-sm text-gray-300 dark:text-gray-600 hover:text-yellow-400 dark:hover:text-yellow-500'
												}
											/>
										)}
									</button>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<span className="text-sm text-gray-900 dark:text-gray-100 truncate">
												{t(formatMethodLabel(method))}
											</span>
										</div>
										{exp != null && (
											<div className="text-xs text-brand-muted mt-0.5">
												{t('Exp')} {t(exp)}
											</div>
										)}
									</div>
									<button
										type="button"
										onClick={() => void handleDelete(method.id)}
										disabled={isDeleting}
										className="shrink-0 ml-3 p-2 text-brand-muted hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50 transition-colors"
										aria-label={t('Delete payment method')}
									>
										{isDeleting ? (
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
										) : (
											<FontAwesomeIcon icon={faTrashCan} className="text-sm" />
										)}
									</button>
								</div>
							);
						})}
					</div>
				)}
			</div>

			{/* Add Payment Method sidebar — same pattern as Cart page */}
			<Sidebar
				panelClassName="bg-brand-surface"
				isOpen={showAddSidebar}
				onClose={() => {
					setShowAddSidebar(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={handleAddSuccess}
						onCancel={() => {
							setShowAddSidebar(false);
						}}
						supportedPaymentMethods={supportedTypes}
					/>
				</div>
			</Sidebar>
		</>
	);
};
